import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { ProductNotesModal } from '@components/Modal/ProductNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { ServiceTaskProgress } from '@components/OrderProcessing/ServiceTaskProgress';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  OrderProcessingMasterData,
  OrderProcessStatus,
  ProductProcessingCrumbSteps,
  ProductType,
  timers,
  WeightType,
} from '@helpers/Constants';
import { FrontendRoutes, ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  ProductProcessingOrderSpecies,
  ProductProcessingProduct,
} from '@helpers/Models';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const ProductProcessedDetails: React.FC = () => {
  const location = useLocation();
  const { speciesId, productName, orderNumber, productId, speciesName } =
    location.state ?? {};
  const [productDetails, setProductDetails] =
    useState<ProductProcessingOrderSpecies>({
      orderId: 0,
      orderSpeciesId: 0,
      orderNumber: '',
      speciesTag: '',
      imageUrl: '',
      orderCheckinInches: 0,
      orderCheckinFeet: 0,
      notes: {
        orderCheckinNotes: '',
        orderSpeciesNotes: '',
        orderServiceNotes: [],
      },
      products: [],
    });
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [showProductNotesModal, setShowProductNotesModal] = useState(false);
  const [productNotesModalMode, setProductNotesModalMode] = useState<
    'view' | 'edit'
  >('view');
  // Add missing state for activeServiceForNotes
  const [activeProductForNotes, setActiveProductForNotes] =
    useState<ProductProcessingProduct | null>(null);

  const [currentStep, setCurrentStep] = useState<'inProduct' | 'completed'>(
    'inProduct',
  );
  /**
   * Effect hook to fetch required data when `speciesId` changes.
   *
   * - If `speciesId` is valid, it fetches:
   *   - Product processing task details via `fetchproductDetails`
   *   - Master package data via `fetchMasterPackages`
   * - If `speciesId` is missing or invalid:
   *   - Displays an error toast message
   *   - Navigates back to the product processing list after a timeout
   *
   * @dependency speciesId
   */
  useEffect(() => {
    if (speciesId) {
      fetchproductDetails();
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.PRODUCT_PROCESSING_LIST);
      }, timers.TOAST_AUTO_CLOSE); //4000ms
    }
  }, [speciesId]);

  useEffect(() => {
    if (productDetails?.products) {
      checkAllProductsCompleted();
    }
  }, [productDetails?.products]);

  /**
   * Fetches product processing task details for the given species ID.
   *
   * @async
   * @function fetchproductDetails
   *
   * Sends a POST request to the `PRODUCT_PROCESSING_TASK_DETAILS` endpoint with `speciesId`.
   * On success, sets the first item from the response data as the active product details.
   * On failure, displays an error toast message and rethrows the error.
   *
   * @returns {Promise<void>} A promise that resolves when the product details are fetched and set.
   *
   */
  const fetchproductDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.PRODUCT_PROCESSING_TASK_DETAILS,
        {
          orderSpeciesId: speciesId,
        },
      );
      setProductDetails(response.data.data[0]);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
      throw error;
    }
  };

  /**
   * Toggles the open/closed state of an accordion section by index.
   *
   * If the index is already in the `openAccordions` array, it will be removed (collapsed).
   * If it's not present, it will be added (expanded).
   *
   * @param {number} index - The index of the accordion section to toggle.
   */
  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /**
   * Navigates to the product in-progress details page in edit mode.
   *
   * Passes the current product context (speciesId, productName, orderNumber, productId)
   * along with `editMode: true` in the navigation state.
   *
   */
  const handleEditMode = (): void => {
    navigate(FrontendRoutes.PRODUCT_INPROGRESS_DETAILS, {
      state: {
        speciesId,
        productName,
        orderNumber,
        editMode: true,
        productId,
        speciesName,
      },
    });
  };

  /**
   * Checks whether all products in the list have been completed.
   *
   * A service is considered completed if its `productStatus` is `2`.
   * If all products are completed, the function updates the current step
   * to `"completed"` by calling `setCurrentStep`.
   */
  const checkAllProductsCompleted = (): void => {
    const allCompleted = productDetails.products.every(
      (product) => product.productStatus === 2,
    );
    if (allCompleted) {
      setCurrentStep('completed');
    }
  };

  /**
   * Prepares and displays the modal to view service notes for the selected product.
   *
   * @param {Product} product - The product for which service notes should be viewed.
   *
   * Sets the selected product as active, configures the modal to 'view' mode,
   * and opens the product notes modal.
   *
   */
  const handleViewProductNotes = (product: ProductProcessingProduct) => {
    setActiveProductForNotes(product);
    setShowProductNotesModal(true);
    setProductNotesModalMode('view');
  };

  const BreadCrumbsProps = {
    steps: ProductProcessingCrumbSteps,
    activeItem: 'processItem_name',
    orderNumber: orderNumber,
    speciesName: speciesName,
    processItem_name: productName,
    type: OrderProcessingMasterData.PRODUCT_PROCESSING,
    stateProp: {
      speciesId,
      productName,
      orderNumber,
      editMode: true,
      productId,
      speciesName,
      orderId: productDetails.orderId,
    },
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={5} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.go(-2)} // navigating to product processing details
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {productName}
              </h2>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="text-primary p-2 font-bold"
              >
                View Notes
              </button>
            </div>
            {showNotesModal && (
              <OrderNotesModal
                speciesName={productDetails?.speciesTag ?? ''}
                onClose={() => setShowNotesModal(false)}
                checkInNotes={
                  productDetails?.notes.orderCheckinNotes != ''
                    ? (productDetails?.notes.orderCheckinNotes ?? '')
                    : errorMessage.NO_NOTES_FOUND
                }
                AdditionalNotes={
                  productDetails?.notes.orderSpeciesNotes != ''
                    ? (productDetails?.notes.orderSpeciesNotes ?? '')
                    : errorMessage.NO_NOTES_FOUND
                }
                ServiceNotes={productDetails.notes.orderServiceNotes}
              />
            )}
          </div>
          {/* Product task progress */}
          <div className="mb-3">
            <ServiceTaskProgress currentStep={currentStep} type="product" />
          </div>
          {/* Order Details */}
          {productDetails.products
            ?.filter((product) => product.orderProductId === productId)
            .map((product, index) => (
              <div
                key={product.orderProductId}
                className="mt-3 p-3 rounded-lg shadow-md bg-[#FFFEFA]"
              >
                <div>
                  <div className="flex justify-between items-center">
                    <h1 className="font-semibold text-xl mt-3">
                      {product.productName}
                      {product.fatPercent > 0 && `, ${product.fatPercent}% Fat`}
                    </h1>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleViewProductNotes(product)}
                        type="button"
                        className="text-primary p-2 font-bold"
                      >
                        View Notes
                      </button>
                      {/* Show edit option only if the product status is not beyond 'completed' (before invoicing) */}
                      {product.productStatus <=
                        OrderProcessStatus.COMPLETED && (
                        <>
                          <span className="text-gray-400">|</span>
                          <button
                            onClick={handleEditMode}
                            type="button"
                            className="text-primary p-2 font-bold"
                          >
                            <FontAwesomeIcon className="mr-1" icon={faPen} />
                            Edit
                          </button>
                          <span className="text-gray-400">|</span>
                          <span className="ml-1">
                            <OrderStatusBadge
                              status={product.productStatus}
                              type="productStatus"
                            />
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3" key={index}>
                  <div className="border rounded-md shadow-sm mb-3">
                    <button
                      type="button"
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex justify-between items-center px-4 py-2 font-semibold text-lg bg-[#F1F1F1]"
                    >
                      <span>Order Details</span>
                      <FontAwesomeIcon
                        className="text-[#000A16]"
                        icon={
                          openAccordions.includes(index)
                            ? faChevronUp
                            : faChevronDown
                        }
                      />
                    </button>
                    {openAccordions.includes(index) && (
                      <div className="bg-[#F1F1F1] rounded-md shadow-md px-3 py-1 border-[#00000033]">
                        <div className="flex items-center justify-between py-2 rounded-md">
                          <div>
                            <span className="text-[#43474E]">
                              {product.productType == ProductType.WEIGHT
                                ? 'Total Weight: '
                                : 'Total Quantity: '}
                            </span>
                            <span className="text-gray-800 font-semibold">
                              {product.productQuantity}{' '}
                              {product.productType == ProductType.WEIGHT
                                ? `${WeightType.LBS}`
                                : ''}
                            </span>
                          </div>
                          {product.productType == ProductType.QUANTITY && (
                            <div>
                              <span className="text-[#43474E] mt-2">
                                Total Estimated weight in Lbs:{' '}
                              </span>
                              <span className="text-gray-800 font-semibold">
                                <span className="text-gray-800 font-semibold">
                                  {(
                                    product.productQuantity * product?.weight
                                  ).toFixed(2)}{' '}
                                  {WeightType.LBS}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>

                        {product.addons?.length > 0 && (
                          <div>
                            <span className="font-semibold mb-2">Add-Ons:</span>
                            <div className="flex flex-col gap-1 py-2 text-[#43474E]">
                              {product.addons.map((addon) => (
                                <span key={addon.addonId}>
                                  {addon.addonName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {product.packing?.length > 0 && (
                          <div>
                            <span className="font-semibold mb-2">
                              Packaging:
                            </span>
                            <div className="space-y-2">
                              {/* Display only non-final packing records when the product status is at least 'in progress' */}
                              {product.packing
                                .filter(
                                  (pack) =>
                                    pack.isFinalPacking === 0 &&
                                    product.productStatus >=
                                      OrderProcessStatus.COMPLETED,
                                )
                                .map((pack) => (
                                  <div
                                    key={pack.orderPackingId}
                                    className="flex justify-between"
                                  >
                                    <span className="w-1/4 text-[#43474E]">
                                      {pack?.packageType}
                                    </span>
                                    <span className="w-1/4 text-center">
                                      <span className="text-[#43474E]">
                                        Size:{' '}
                                      </span>
                                      {pack?.packageSize}{' '}
                                      <span className="text-black">Lbs</span>
                                    </span>
                                    {product.packing.length > 1 && (
                                      <span className="w-1/4 text-center">
                                        <span className="text-[#43474E]">
                                          Pkg Split:
                                        </span>{' '}
                                        <span className="text-black">
                                          {pack.packingPercentage}%
                                        </span>
                                      </span>
                                    )}
                                    <span className="w-1/4 text-right">
                                      <span className="text-[#43474E]">
                                        Price:{' '}
                                      </span>
                                      <span className="text-black">
                                        ${pack.packingPrice}
                                      </span>
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                        {product.orderPackingNotes && (
                          <div className="mt-3 mb-2">
                            <h4 className="font-medium mb-2">
                              Packaging Notes:
                            </h4>
                            <span className="text-[#43474E]">
                              {product.orderPackingNotes}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Final measurements */}
                <div className="rounded-md shadow-md mt-5 p-3 border border-[#00000033]">
                  <span className="font-semibold">Final Measurements</span>
                  <div className="flex items-center justify-between py-2 rounded-md">
                    <div>
                      <span className="text-[#43474E]">
                        {product.productType == ProductType.WEIGHT
                          ? 'Total Weight: '
                          : 'Total Quantity: '}
                      </span>
                      <span className="text-gray-800 font-semibold">
                        {product.productFinalQuantity}{' '}
                        {product.productType == ProductType.WEIGHT
                          ? `${WeightType.LBS}`
                          : ''}
                      </span>
                    </div>
                    {product.productType == ProductType.QUANTITY && (
                      <div>
                        <span className="text-[#43474E] mt-2">
                          Total Estimated weight in Lbs:{' '}
                        </span>
                        <span className="text-gray-800 font-semibold">
                          <span className="text-gray-800 font-semibold">
                            {(
                              product.productFinalQuantity * product?.weight
                            ).toFixed(2)}{' '}
                            {WeightType.LBS}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  {product.addons?.length > 0 && (
                    <div>
                      <span className="font-semibold mb-2">Add-Ons:</span>
                      <div className="flex flex-col gap-1 py-2 text-[#43474E]">
                        {product.addons.map((addon) => (
                          <span key={addon.addonId}>{addon.addonName}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 1 means order product is in progress, 2 means order product is completed 
                                isFinalPacking flag - 1 represents the finalized packaging and 0 means the packagings 
                                that were added while order creation. */}
                  {product.packing?.length > 0 && (
                    <div>
                      <span className="font-semibold mb-2">Packaging:</span>
                      <div className="space-y-2">
                        {product.packing.map((pack) => {
                          const isVisible =
                            (pack.isFinalPacking === 1 &&
                              product.productStatus === 2) ||
                            (pack.isFinalPacking === 0 &&
                              product.productStatus !== 2);

                          if (!isVisible) return null;

                          return (
                            <div
                              key={pack.orderPackingId}
                              className="flex justify-between"
                            >
                              <span className="w-1/4 text-[#43474E]">
                                {pack.packageType}
                              </span>

                              <span className="w-1/4 text-center">
                                <span className="text-[#43474E]">Size: </span>
                                {pack.packageSize}{' '}
                                <span className="text-black">Lbs</span>
                              </span>

                              {product.packing.length > 1 && (
                                <span className="w-1/4 text-center">
                                  <span className="text-[#43474E]">
                                    Pkg Split:{' '}
                                  </span>
                                  <span className="text-black">
                                    {pack.packingPercentage}%
                                  </span>
                                </span>
                              )}

                              {product.productStatus === 2 && (
                                <span className="w-1/4 text-right">
                                  <span className="text-[#43474E]">
                                    Price:{' '}
                                  </span>
                                  <span className="text-black">
                                    ${pack.packingPrice}
                                  </span>
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Packaging Notes if any */}
                  {product.orderPackingNotes && (
                    <div className="mt-3">
                      <h4 className="font-medium mb-2">Packaging Notes:</h4>
                      <span className="text-[#43474E]">
                        {product.orderPackingNotes}
                      </span>
                    </div>
                  )}
                </div>
                {/* Start date & end date */}
                {product.productProcessDetail && (
                  <div className="p-1 mt-3 flex justify-between">
                    {product.productProcessDetail.productStartDate && (
                      <span className="text-gray-600">
                        Start date:{' '}
                        {new Date(
                          product.productProcessDetail.productStartDate,
                        ).toLocaleDateString('en-US')}
                      </span>
                    )}
                    {product.productProcessDetail.productEndDate && (
                      <span className="text-gray-600">
                        End date:{' '}
                        {new Date(
                          product.productProcessDetail.productEndDate,
                        ).toLocaleDateString('en-US')}
                      </span>
                    )}
                  </div>
                )}
                <div className="w-full">
                  <span className="inline-block w-full bg-[#CCEECC] border border-[#2D5B4A] p-2 mt-3 text-[#2D5B4A] text-center font-semibold rounded-md">
                    Product Processed & Packed{' '}
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-[#33795F]"
                    />
                  </span>
                </div>
                {/* Product Processing Notes */}
                <ProductNotesModal
                  isOpen={showProductNotesModal}
                  onClose={() => {
                    setShowProductNotesModal(false);
                    setActiveProductForNotes(null);
                    fetchproductDetails();
                  }}
                  productNotes={activeProductForNotes?.orderProductNotes ?? []}
                  orderId={productDetails?.orderId ?? 0}
                  orderSpeciesId={productDetails?.orderSpeciesId ?? 0}
                  orderProductId={activeProductForNotes?.orderProductId ?? 0}
                  showToastMessage={showToastMessage}
                  mode={productNotesModalMode}
                />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
